<script setup lang="ts">
import { addMoney, formatDate, isZeroMoney, type LocalDate, moneyProportion, zeroMoney } from '@taler/domain'
import { KindIcon } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import KpiChangeIndicator from '@/features/dashboard/components/KpiChangeIndicator.vue'
import { type MonthComparison, netMonthToDate, outflowMonthToDate } from '../internal/month-comparison.ts'
import { TILE_CLASS } from '../internal/tile.ts'
import type { Dashboard } from '../internal/types.ts'

/**
 * This month so far: income, spending and net, each with its change against the same point
 * last month. The window on screen is the one the comparison actually covers.
 */
const props = defineProps<{
  dashboard: Dashboard
  comparison: MonthComparison | undefined
  from: LocalDate
  until: LocalDate
  previousFrom: LocalDate
  previousUntil: LocalDate
}>()

const { t, locale } = useI18n()
const localeCode = computed(() => locale.value as 'en' | 'hu')

const windowText = computed(() =>
  t('dashboard.windowRange', {
    from: formatDate(props.from, localeCode.value),
    until: formatDate(props.until, localeCode.value),
  }),
)
const previousWindowText = computed(() =>
  t('dashboard.windowRange', {
    from: formatDate(props.previousFrom, localeCode.value),
    until: formatDate(props.previousUntil, localeCode.value),
  }),
)

const headline = computed(() =>
  props.comparison && props.comparison.net.direction < 0
    ? t('dashboard.headlineBehind')
    : t('dashboard.headlineOkay'),
)

const rows = computed(() => [
  {
    key: 'income',
    kind: 'INCOME' as const,
    label: t('dashboard.income'),
    short: t('dashboard.incomeShort'),
    tone: 'text-[color:var(--color-income)]',
    money: props.dashboard.incomeMonthToDate,
    change: props.comparison?.income,
    favourable: 'up' as const,
  },
  {
    key: 'expense',
    kind: 'EXPENSE' as const,
    label: t('dashboard.expense'),
    short: t('dashboard.expenseShort'),
    tone: 'text-[color:var(--color-expense)]',
    money: outflowMonthToDate(props.dashboard),
    change: props.comparison?.expense,
    favourable: 'down' as const,
  },
  {
    key: 'net',
    kind: undefined,
    label: t('dashboard.netResult'),
    short: t('dashboard.netResultShort'),
    tone: 'text-[color:var(--color-text)]',
    money: netMonthToDate(props.dashboard),
    change: undefined,
    favourable: 'up' as const,
  },
])

// Income's share of the month's gross movement, for the two-colour bar: both figures are
// the server's, in the one display currency the response carries.
const incomeShare = computed(() => {
  const income = props.dashboard.incomeMonthToDate
  const total = addMoney(income, props.dashboard.expenseMonthToDate)
  if (isZeroMoney(total)) return undefined
  return moneyProportion(income, zeroMoney(total.currency), total)
})
</script>

<template>
  <section :class="TILE_CLASS" :aria-label="t('dashboard.thisMonth')">
    <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
      {{ t('dashboard.thisMonth') }}
    </p>
    <h2
      v-if="comparison"
      class="font-display text-[length:var(--text-step-5)] font-semibold text-[color:var(--color-text)]"
    >
      {{ headline }}
    </h2>
    <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
      {{ t('dashboard.comparedWith', { current: windowText, previous: previousWindowText }) }}
    </p>
    <div class="grid grid-cols-1 gap-[var(--space-group)] sm:grid-cols-3">
      <div
        v-for="row in rows"
        :key="row.key"
        class="flex min-w-0 flex-col gap-2 rounded-[var(--radius-surface)] border border-[color:var(--color-border)] p-[var(--space-inline)]"
      >
        <div class="flex items-center gap-2">
          <span
            v-if="row.kind"
            class="flex size-7 items-center justify-center rounded-[var(--radius-control)] bg-[color:var(--color-surface-sunken)]"
          >
            <KindIcon :kind="row.kind" :label="row.label" />
          </span>
          <p aria-hidden="true" class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
            {{ row.short }}
          </p>
        </div>
        <p
          class="whitespace-nowrap text-[length:var(--text-step-4)] font-semibold tabular-nums"
          :class="row.tone"
        >
          <span class="sr-only">{{ row.label }}:</span>
          <MoneyAmount :value="row.money" sign size="medium" />
        </p>
        <KpiChangeIndicator
          v-if="row.change"
          :change="row.change"
          :favourable="row.favourable"
          :caption="t('dashboard.vsSamePointLastMonth')"
        />
      </div>
    </div>
    <div
      v-if="incomeShare !== undefined"
      aria-hidden="true"
      class="flex h-1.5 gap-0.5 overflow-hidden rounded-full"
    >
      <span class="bg-[color:var(--color-income)]" :style="{ flexGrow: incomeShare }" />
      <span class="bg-[color:var(--color-expense)]" :style="{ flexGrow: 1 - incomeShare }" />
    </div>
  </section>
</template>
